import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from 'chameleon/ui-stack/ui/base/button';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';
import withFormController from '../withFormController';
import { getFormIdField } from '../../utils';

/**
 * This component handles SelectAll / Undo functionality for manage modal.
 * Behavior described in https://academicmerit.atlassian.net/browse/FY-17690
 * */
const SelectAllUndo = ({
  assignmentId,
  students,
  disabledStudentIds,
  selectedStudents,
  onSelectAll,
  onUndo,
  markFormAsDirty,
  isFormDirty,
}) => {
  /*
   * `availableStudents` keeps all the students that can be SELECTED/UNSELECTED.
   * Disabled students are not included.
   * */
  const [availableStudents, setAvailableStudents] = useState([]);
  /*
   * `selectedFormStudents` keeps all the students within the form (already selected) that can be SELECTED/UNSELECTED.
   * Disabled students are not included.
   * */
  const [selectedFormStudents, setSelectedFormStudents] = useState([]);
  const [isSelectAllAction, setIsSelectAllAction] = useState(false);
  const handlerText = useRef('Select All');

  useEffect(() => {
    const newAvailableStudents = students.filter(
      (s) => !disabledStudentIds.includes(s.id),
    );
    const newSelectedFormStudents = selectedStudents.filter(
      (s) => !disabledStudentIds.includes(s.studentId),
    );
    const isUndoOption =
      newAvailableStudents.length &&
      (isFormDirty
        ? newAvailableStudents.length === newSelectedFormStudents.length
        : false);
    setAvailableStudents(newAvailableStudents);
    setSelectedFormStudents(newSelectedFormStudents);
    setIsSelectAllAction(!isUndoOption);
    handlerText.current = isUndoOption ? 'Undo' : 'Select All';
  }, [students, disabledStudentIds, selectedStudents]);

  const undo = () => {
    let studentsToUndo = selectedStudents.filter((s) => s.removeOnUndo);
    if (studentsToUndo.length === 0) {
      studentsToUndo = [selectedStudents[selectedStudents.length - 1]];
    }
    onUndo && onUndo(studentsToUndo);
  };

  const selectAll = () => {
    const selectedFormStudentIds = selectedFormStudents.map((s) => s.studentId);
    const studentsToSelect = availableStudents
      .filter((s) => !selectedFormStudentIds.includes(s.id))
      .map((s) => ({
        ...s,
        id: getFormIdField(s.id, s.sectionId, assignmentId),
        studentId: s.id,
        sectionId: s.sectionId,
        assignmentId,
        removeOnUndo: true,
      }));

    if (studentsToSelect.length > 0) {
      !isFormDirty && markFormAsDirty();
      onSelectAll && onSelectAll(studentsToSelect);
    }
  };

  const handleClick = (event) => {
    const isClickEvent = !event.keyCode;
    const isKeyDownEvent = [13, 32].includes(event.keyCode);

    if (isClickEvent || isKeyDownEvent) {
      isSelectAllAction ? selectAll() : undo();
      event.stopPropagation();
      event.preventDefault();
    }
  };

  return (
    <Button
      className="-ml-2 my-4"
      data-test-id={DATA_TEST_IDS.SELECT_ALL_STUDENTS}
      onClick={handleClick}
      aria-label={handlerText.current}
      isSecondary
    >
      {handlerText.current}
    </Button>
  );
};

SelectAllUndo.displayName = 'SelectAllUndo';

SelectAllUndo.propTypes = {
  assignmentId: PropTypes.string.isRequired,
  students: PropTypes.array,
  disabledStudentIds: PropTypes.array,
  selectedStudents: PropTypes.array,
  onSelectAll: PropTypes.func.isRequired,
  onUndo: PropTypes.func.isRequired,
  formInfo: PropTypes.shape({
    name: PropTypes.string.isRequired,
    dispatch: PropTypes.func.isRequired,
    students: PropTypes.array.isRequired,
  }).isRequired,
  markFormAsDirty: PropTypes.func.isRequired,
  isFormDirty: PropTypes.bool,
};

SelectAllUndo.defaultProps = {
  students: [],
  disabledStudentIds: [],
  selectedStudents: [],
  isFormDirty: false,
};

export default withFormController(SelectAllUndo);
